import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Paper,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormGroup,
    TextField,
    Slider,
    Grid,
    Container,
    FormHelperText
} from '@mui/material';
import api from './api'; // Your API helper
import config from './config'; // Your config file

const getAuthDetails = () => {
    return {
        userId: localStorage.getItem('userId'),
        token: localStorage.getItem('token')
    };
};

const sectionInstructions = {
    'Attitudinal Communication Styles': "This first assessment will help you better understand how your attitudes shape your communication style. For each statement set below, choose two of the four sentence ending options that come closest to matching your beliefs.",
    'Work Environment Preferences': "This assessment will help you better understand your work style and preferences regarding the organizational cultures in which you work with others. Consider your own preferences at work or in a team environment and indicate your level of agreement with each statement below.",
    'Adjectives': "This section will help you better understand your own professional identity and will serve as an important starting point as you clarify your ideal professional reputation moving forward. Please select up to seven descriptors from the list below that you believe best describe you in a work or team environment.",
    'Informal Team Roles': "This section will help you think through the informal role you are most likely to play in a team work setting."
};

function SelfPerceptionInventorySurvey({ onSpiComplete, cohortId }) {
    const navigate = useNavigate();
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [responses, setResponses] = useState({});
    const [activeStep, setActiveStep] = useState(0); 
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    // eslint-disable-next-line no-unused-vars
    const { userId } = getAuthDetails();

    useEffect(() => {
        const fetchQuestions = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get(`${config.backendUrl}/api/spi/questions`);
                if (response.data && Array.isArray(response.data.sections)) {
                    const fetchedSections = response.data.sections;
                    setSections(fetchedSections);
                    const initialResponses = {};
                    fetchedSections.forEach(section => {
                        if (section && Array.isArray(section.questions)) {
                            section.questions.forEach(q => {
                                if (q && typeof q.question_id !== 'undefined') {
                                    if (q.question_type_spi === 'multi_select_N' || q.question_type_spi === 'select_two_from_set') {
                                        initialResponses[q.question_id] = [];
                                    } else if (q.question_type_spi === 'scale_0_100') {
                                        initialResponses[q.question_id] = 0; 
                                    } else if (q.question_type_spi === 'likert_0_4') {
                                        initialResponses[q.question_id] = 2; 
                                    }
                                    else {
                                        initialResponses[q.question_id] = '';
                                    }
                                } else {
                                    console.warn("Encountered invalid question object during initialResponses setup:", q);
                                }
                            });
                        } else {
                            console.warn("Encountered section with invalid 'questions' array during initialResponses setup:", section);
                        }
                    });
                    setResponses(initialResponses);
                } else {
                    console.error("Invalid data format for SPI questions, 'sections' array not found:", response.data);
                    throw new Error("Invalid data format for SPI questions. Expected 'sections' array.");
                }
            } catch (err) {
                console.error("Error fetching SPI questions:", err);
                setError(err.response?.data?.message || err.message || "Failed to load survey questions.");
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, []);

    const handleResponseChange = useCallback((questionId, questionType, value, optionId = null) => {
        setResponses(prevResponses => {
            const newResponsesState = { ...prevResponses };
            const question = sections
                .flatMap(s => (s && Array.isArray(s.questions) ? s.questions : []))
                .find(q => q && q.question_id === questionId);

            if (!question) {
                console.warn(`handleResponseChange: Question with ID ${questionId} not found in sections.`);
                return prevResponses;
            }

            if (questionType === 'multi_select_N' || questionType === 'select_two_from_set') {
                let currentSelection = prevResponses[questionId] ? [...prevResponses[questionId]] : [];
                const maxSelections = questionType === 'select_two_from_set' ? 2 : question.max_selections;

                if (value) { 
                    if (optionId !== null && !currentSelection.includes(optionId)) {
                        if (maxSelections && currentSelection.length >= maxSelections) {
                            if (questionType === 'select_two_from_set') {
                                return prevResponses; 
                            }
                        } else {
                            currentSelection.push(optionId);
                        }
                    }
                } else { 
                    if (optionId !== null) {
                        const index = currentSelection.indexOf(optionId);
                        if (index > -1) {
                            currentSelection.splice(index, 1);
                        }
                    }
                }
                newResponsesState[questionId] = currentSelection;
            } else {
                newResponsesState[questionId] = value;
            }

            setValidationErrors(prevErrors => {
                const questionIdStr = questionId.toString();
                const currentErrorForQuestion = prevErrors[questionIdStr];
                let shouldClear = false;
                const updatedResponseForValidation = newResponsesState[questionId];

                if (questionType === 'select_two_from_set') {
                    shouldClear = Array.isArray(updatedResponseForValidation) && updatedResponseForValidation.length === 2;
                } else if (questionType === 'multi_select_N') {
                    shouldClear = Array.isArray(updatedResponseForValidation) && 
                                  updatedResponseForValidation.length > 0 && 
                                  updatedResponseForValidation.length <= (question.max_selections || Infinity);
                } else if (questionType === 'single_select' || questionType === 'likert_0_4') { 
                    shouldClear = updatedResponseForValidation !== '' && 
                                  updatedResponseForValidation !== null && 
                                  updatedResponseForValidation !== undefined;
                     if (questionType === 'likert_0_4' && typeof updatedResponseForValidation === 'number') shouldClear = true;
                }


                if (shouldClear && currentErrorForQuestion) {
                    const nextErrors = { ...prevErrors };
                    delete nextErrors[questionIdStr];
                    return nextErrors; 
                }
                return prevErrors; 
            });
            
            return newResponsesState; 
        });
    }, [sections]); 

    const checkStepValidity = useCallback((stepIndex) => {
        if (!sections[stepIndex] || !Array.isArray(sections[stepIndex].questions)) {
            return { isValid: true, errors: {} }; 
        }
        const section = sections[stepIndex];
        let allValidInStep = true;
        const stepErrors = {};

        for (const q of section.questions) {
            if (!q || typeof q.question_id === 'undefined') continue;

            const response = responses[q.question_id]; 
            let isValid = true;
            let errorMsg = null;

            if (q.question_type_spi === 'select_two_from_set') {
                if (!Array.isArray(response) || response.length !== 2) {
                    isValid = false;
                    errorMsg = "Please select exactly two options.";
                }
            } else if (q.question_type_spi === 'multi_select_N') {
                if (!Array.isArray(response) || response.length === 0 || (q.max_selections && response.length > q.max_selections)) {
                    isValid = false;
                    errorMsg = `Please select between 1 and ${q.max_selections || 'any number of'} options.`;
                }
            } else if (q.question_type_spi === 'single_select') {
                 if (response === '' || response === null || response === undefined || (Array.isArray(response) && response.length === 0) ) {
                    isValid = false;
                    errorMsg = "Please select an option.";
                 }
            } else if (q.question_type_spi === 'likert_0_4') { 
                // Sliders now, default to 2. No specific validation unless a value must be changed from default.
            }
            
            if (!isValid) {
                allValidInStep = false;
                stepErrors[q.question_id] = errorMsg;
            }
        }
        return { isValid: allValidInStep, errors: stepErrors };
    }, [sections, responses]); 

    const validateAndRecordStepErrors = useCallback((stepIndex) => {
        const { isValid, errors } = checkStepValidity(stepIndex);
        setValidationErrors(prev => {
            let changed = false;
            const newErrors = {...prev};
            const sectionQuestions = sections[stepIndex]?.questions || [];
            
            for (const qId in errors) {
                if (newErrors[qId] !== errors[qId]) {
                    newErrors[qId] = errors[qId];
                    changed = true;
                }
            }
            sectionQuestions.forEach(q => {
                if (q && !errors[q.question_id] && newErrors[q.question_id]) {
                    delete newErrors[q.question_id];
                    changed = true;
                }
            });

            return changed ? newErrors : prev;
        });
        return isValid;
    }, [checkStepValidity, sections]); 
    
    const handleNext = () => {
        if (validateAndRecordStepErrors(activeStep)) { 
            if (activeStep < sections.length - 1) {
                 setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
            window.scrollTo(0, 0); 
        } else {
            alert("Please correct the highlighted errors before proceeding.");
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        window.scrollTo(0, 0); 
    };

    const handleSubmit = async () => {
        let allStepsValidOverall = true;
        const allEncounteredValidationErrors = {};
        for (let i = 0; i < sections.length; i++) {
            if (sections[i] && Array.isArray(sections[i].questions)) {
                const { isValid, errors } = checkStepValidity(i); 
                if (!isValid) { 
                    allStepsValidOverall = false;
                    Object.assign(allEncounteredValidationErrors, errors);
                }
            }
        }
        
        setValidationErrors(allEncounteredValidationErrors); 

        if (!allStepsValidOverall) {
             alert("Please ensure all questions are answered correctly according to instructions. Check all sections for errors.");
             setIsSubmitting(false);
             return;
        }
        
        setIsSubmitting(true);
        setError(null);

        const formattedResponses = Object.entries(responses).map(([questionId, value]) => {
            const question = sections
                .flatMap(s => (s && Array.isArray(s.questions) ? s.questions : []))
                .find(q => q && q.question_id === parseInt(questionId));
            if (!question) return null;

            if (question.question_type_spi === 'multi_select_N' || question.question_type_spi === 'select_two_from_set' || question.question_type_spi === 'single_select') {
                return {
                    spi_question_id: parseInt(questionId),
                    selected_option_ids: Array.isArray(value) ? value.map(id => parseInt(id, 10)) : (value && value !== '' ? [parseInt(value)] : []), 
                    response_value_numeric: null
                };
            } else { 
                return {
                    spi_question_id: parseInt(questionId),
                    selected_option_ids: null,
                    response_value_numeric: (value === '' || value === null || typeof value === 'undefined') ? null : parseInt(value, 10) 
                };
            }
        }).filter(r => r !== null);

        try {
            await api.post(`${config.backendUrl}/api/spi/responses`, {
                cohort_id: cohortId,
                responses: formattedResponses
            });
            await api.post(`${config.backendUrl}/api/spi/complete`, {
                cohort_id: cohortId
            });
            
            onSpiComplete(); 
            navigate('/dashboard', { state: { message: 'Self-Perception Inventory submitted successfully!' } });

        } catch (err) {
            console.error("Error submitting SPI responses:", err);
            setError(err.response?.data?.message || err.message || "Failed to submit responses.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderQuestion = (question) => {
        if (!question || typeof question.question_id === 'undefined') {
            return <Typography color="error" sx={{textAlign: 'left'}}>Error: Invalid question data encountered.</Typography>;
        }

        const responseValue = responses[question.question_id];
        const questionError = validationErrors[question.question_id];

        switch (question.question_type_spi) {
            case 'select_two_from_set':
            case 'multi_select_N':
                if (!Array.isArray(question.options)) {
                    console.warn("Question options are not an array for multi-select/select-two:", question);
                    return <Typography color="error" sx={{textAlign: 'left'}}>Error: Options not available for this question.</Typography>;
                }
                const currentSelections = Array.isArray(responseValue) ? responseValue : [];
                const maxSelections = question.question_type_spi === 'select_two_from_set' ? 2 : question.max_selections;
                
                const isAdjectivesSection = question.section_item_key === 'adjectives_selection';
                const AdjectiveWrapper = isAdjectivesSection ? Grid : Box; 
                const adjectiveItemProps = isAdjectivesSection ? { item: true, xs: 12, sm: 6, md: 4 } : {};

                return (
                    <FormControl component="fieldset" error={!!questionError} sx={{ mt: 1, mb: 2, width: '100%' }}>
                        <FormLabel component="legend" sx={{ textAlign: 'left', width: '100%', mb: 1, color: 'rgba(0, 0, 0, 0.6)' }}>{question.question_text}</FormLabel>
                        <FormGroup sx={{ width: '100%'}}>
                            <AdjectiveWrapper container={isAdjectivesSection} spacing={isAdjectivesSection ? 1 : 0}>
                                {question.options.map(opt => {
                                    if (!opt || typeof opt.option_id === 'undefined') {
                                        console.warn("Invalid option in question.options:", opt, "for question:", question.question_id);
                                        return null; 
                                    }
                                    return (
                                        <AdjectiveWrapper key={opt.option_id} {...adjectiveItemProps} sx={{display: 'flex', width: '100%'}}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={currentSelections.includes(opt.option_id)}
                                                        onChange={(e) => handleResponseChange(question.question_id, question.question_type_spi, e.target.checked, opt.option_id)}
                                                        disabled={maxSelections != null && currentSelections.length >= maxSelections && !currentSelections.includes(opt.option_id) && question.question_type_spi !== 'select_two_from_set'}
                                                        sx={{paddingRight: '4px'}}
                                                    />
                                                }
                                                label={<Typography sx={{ textAlign: 'left' }}>{opt.option_text}</Typography>}
                                                sx={{ width: '100%', mr:0 }}
                                            />
                                        </AdjectiveWrapper>
                                    );
                                })}
                            </AdjectiveWrapper>
                        </FormGroup>
                        {questionError && <FormHelperText sx={{ textAlign: 'left', marginLeft: '14px' }}>{questionError}</FormHelperText>}
                        {!questionError && question.question_type_spi === 'select_two_from_set' && <FormHelperText sx={{ textAlign: 'left', marginLeft: '14px' }}>Select exactly two options.</FormHelperText>}
                        {!questionError && question.question_type_spi === 'multi_select_N' && question.max_selections && <FormHelperText sx={{ textAlign: 'left', marginLeft: '14px' }}>Select up to {question.max_selections} options.</FormHelperText>}
                    </FormControl>
                );
            case 'likert_0_4': 
                const sliderMarks = [
                    { value: 0, label: <Typography variant="caption" sx={{textAlign:'center', lineHeight: '1.2', display: 'block', width: '60px' }}>Totally<br/>Disagree</Typography> },
                    { value: 1, label: <Typography variant="caption">Disagree</Typography> },
                    { value: 2, label: <Typography variant="caption" sx={{textAlign:'center', lineHeight: '1.2', display: 'block', width: '70px'}}>Neither Agree<br/>Nor Disagree</Typography> },
                    { value: 3, label: <Typography variant="caption">Agree</Typography> },
                    { value: 4, label: <Typography variant="caption" sx={{textAlign:'center', lineHeight: '1.2', display: 'block', width: '60px'}}>Totally<br/>Agree</Typography> },
                ];
                return (
                    <Box sx={{ mt: 1, mb: 3, width: '100%', px: {xs: 1, sm: 2} }}> {/* Reduced overall padding slightly */}
                        <Typography gutterBottom id={`slider-label-${question.question_id}`} sx={{ textAlign: 'left', color: 'rgba(0, 0, 0, 0.6)', mb: 1 }}>
                            {question.question_text}
                        </Typography>
                        <Box sx={{ width: '85%', mx: 'auto' }}> {/* Wrapper to control slider width and center it */}
                            <Slider
                                value={typeof responseValue === 'number' ? responseValue : 2} 
                                onChange={(e, newValue) => handleResponseChange(question.question_id, question.question_type_spi, newValue)}
                                aria-labelledby={`slider-label-${question.question_id}`}
                                valueLabelDisplay="auto" 
                                step={1}
                                marks={sliderMarks}
                                min={0}
                                max={4}
                                sx={{
                                    mt: 0, 
                                    mb: 8, 
                                    '& .MuiSlider-markLabel': { 
                                        top: '32px', 
                                        fontSize: '0.65rem', // Further reduced font size for mark labels
                                        whiteSpace: 'normal', // Allow wrapping
                                    },
                                }}
                            />
                        </Box>
                        {questionError && <FormHelperText error sx={{ textAlign: 'left' }}>{questionError}</FormHelperText>}
                    </Box>
                );
            case 'single_select': 
                 if (!Array.isArray(question.options)) {
                    console.warn("Question options are not an array for single_select:", question);
                    return <Typography color="error" sx={{textAlign: 'left'}}>Error: Options not available for this question.</Typography>;
                }
                return (
                    <FormControl component="fieldset" error={!!questionError} sx={{ mt: 1, mb: 2, width: '100%' }}>
                        <FormLabel component="legend" sx={{ textAlign: 'left', width: '100%', mb: 1, color: 'rgba(0, 0, 0, 0.6)' }}>{question.question_text}</FormLabel>
                        <RadioGroup
                            aria-label={question.question_text}
                            name={`question-${question.question_id}`}
                            value={responseValue ? responseValue.toString() : ''} 
                            onChange={(e) => handleResponseChange(question.question_id, question.question_type_spi, parseInt(e.target.value))}
                        >
                            {question.options.map(opt => {
                                 if (!opt || typeof opt.option_id === 'undefined') {
                                    console.warn("Invalid option in single_select question.options:", opt, "for question:", question.question_id);
                                    return null;
                                }
                                return (
                                    <FormControlLabel 
                                        key={opt.option_id} 
                                        value={opt.option_id.toString()} 
                                        control={<Radio sx={{alignSelf: 'flex-start', mt: '6px', pt:0, pb:0}} />} 
                                        label={<Typography sx={{ textAlign: 'left', py: '9px' }}>{opt.option_text}</Typography>} 
                                        sx={{ 
                                            width: '100%', 
                                            mr: 0, 
                                            alignItems: 'flex-start', 
                                            mb: 0.5 
                                        }}
                                    />
                                );
                            })}
                        </RadioGroup>
                        {questionError && <FormHelperText sx={{ textAlign: 'left', marginLeft: '14px' }}>{questionError}</FormHelperText>}
                    </FormControl>
                );
            case 'scale_0_100':
                return (
                    <Box sx={{ mt: 1, mb: 3, width: '100%' }}>
                        <Typography gutterBottom id={`slider-label-${question.question_id}`} sx={{ textAlign: 'left', color: 'rgba(0, 0, 0, 0.6)' }}>{question.question_text}</Typography>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs>
                                <Slider
                                    value={typeof responseValue === 'number' ? responseValue : 0}
                                    onChange={(e, newValue) => handleResponseChange(question.question_id, question.question_type_spi, newValue)}
                                    aria-labelledby={`slider-label-${question.question_id}`}
                                    valueLabelDisplay="auto"
                                    step={1}
                                    marks
                                    min={0}
                                    max={100}
                                />
                            </Grid>
                            <Grid item>
                                <TextField
                                    value={typeof responseValue === 'number' ? responseValue : 0}
                                    size="small"
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value, 10);
                                        handleResponseChange(question.question_id, question.question_type_spi, isNaN(val) ? 0 : Math.max(0, Math.min(100, val)));
                                    }}
                                    inputProps={{
                                        step: 1,
                                        min: 0,
                                        max: 100,
                                        type: 'number',
                                        'aria-labelledby': `slider-label-${question.question_id}`,
                                    }}
                                    sx={{ width: '70px' }}
                                    error={!!questionError}
                                />
                            </Grid>
                        </Grid>
                        {questionError && <FormHelperText error sx={{ textAlign: 'left' }}>{questionError}</FormHelperText>}
                    </Box>
                );
            default:
                return <Typography sx={{ textAlign: 'left' }}>Unsupported question type: {question.question_type_spi}</Typography>;
        }
    };


    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }
    if (!loading && !error && (!Array.isArray(sections) || sections.length === 0)) {
        return <Alert severity="info" sx={{ m: 2 }}>No survey questions are currently available or an issue occurred loading them.</Alert>;
    }

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Self-Perception Inventory
                </Typography>
                
                <Box>
                    {sections.map((section, index) => {
                        if (!section || !Array.isArray(section.questions)) {
                            console.warn("Skipping render for invalid section structure in Stepper:", section);
                            return null; 
                        }
                        const isStepActive = activeStep === index;
                        const currentStepHasErrors = isStepActive && section.questions.some(q => q && typeof q.question_id !== 'undefined' && !!validationErrors[q.question_id]);
        
                        return (
                            <Paper 
                                key={section.section_name || `section-${index}`} 
                                elevation={isStepActive ? 4 : 1} 
                                sx={{ mb: isStepActive ? 2 : 1, overflow: 'hidden', transition: 'all 0.3s ease-in-out' }}
                            >
                                <Box 
                                    onClick={() => setActiveStep(index)}
                                    sx={{ 
                                        p: 2, 
                                        cursor: 'pointer', 
                                        backgroundColor: isStepActive ? 'primary.main' : (activeStep > index && !section.questions.some(q => q && !!validationErrors[q.question_id]) ? 'grey.200' : 'action.hover'), 
                                        // MODIFICATION: The 'color' property has been REMOVED from this sx prop.
                                        borderBottom: isStepActive ? 'none' : '1px solid rgba(0,0,0,0.12)',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderRadius: '4px',
                                        borderBottomLeftRadius: isStepActive ? '0' : '4px',
                                        borderBottomRightRadius: isStepActive ? '0' : '4px',
                                    }}
                                >
                                    {/* The Typography component now controls its own color */}
                                    <Typography sx={{ 
                                        fontWeight: isStepActive ? 'bold' : 'normal',
                                        // MODIFICATION: The conditional color logic is now here.
                                        color: isStepActive ? '#f9f8fa' : 'text.primary' 
                                    }}>
                                        {section.section_name || `Section ${index + 1}`}
                                    </Typography>
                                    {currentStepHasErrors && isStepActive && <Typography variant="caption" sx={{color: isStepActive ? 'error.light' : 'error.main'}}> (Errors)</Typography>}
                                </Box>
                                {/* ... The rest of your component logic inside the Paper ... */}

                                {isStepActive && (
                                    <Box sx={{ p:2, borderTop: '1px solid rgba(0,0,0,0.12)'}}>
                                        <Box sx={{ mb: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                            {sectionInstructions[section.section_name] && (
                                                    <Typography variant="body1" sx={{ textAlign: 'left', whiteSpace: 'pre-line' }}>
                                                        {sectionInstructions[section.section_name]}
                                                    </Typography>
                                            )}
                                            {section.section_name === 'Personal Motivators and Values' && !sectionInstructions[section.section_name] && (
                                                <>
                                                    <Typography variant="body1" paragraph sx={{ textAlign: 'left', whiteSpace: 'pre-line' }}>
                                                        Every professional is motivated by varying and unique combinations of status, social, financial, and decision-making interests based on what they value, what motivates them, and what they prefer about the teams with which they work.
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ textAlign: 'left', whiteSpace: 'pre-line' }}>
                                                        For each of the ten factors below, select the degree to which you agree with each statement, with 0 being no agreement at all and 100 being full and complete agreement.
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>

                                        {section.questions
                                            .filter(q => q && typeof q.question_id !== 'undefined') 
                                            .map(q => (
                                                <Box key={q.question_id} sx={{ 
                                                    mb: 2, 
                                                    pl: validationErrors[q.question_id] ? 0.8 : 1, 
                                                    pt: 0.5, 
                                                    borderLeft: validationErrors[q.question_id] ? '3px solid red' : 'none',
                                                    backgroundColor: validationErrors[q.question_id] ? 'rgba(255, 0, 0, 0.05)' : 'transparent',
                                                    borderRadius: validationErrors[q.question_id] ? '4px' : '0',
                                                 }}>
                                                    {renderQuestion(q)}
                                                </Box>
                                        ))}
                                        <Box sx={{ mb: 2, mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                                            <Button
                                                disabled={activeStep === 0}
                                                onClick={handleBack}
                                                sx={{ mt: 1, mr: 1 }}
                                            >
                                                Back
                                            </Button>
                                            <Button
                                                variant="contained"
                                                onClick={activeStep === sections.length - 1 ? handleSubmit : handleNext}
                                                sx={{ mt: 1, mr: 1 }}
                                                disabled={isSubmitting}
                                            >
                                                {activeStep === sections.length - 1 ? (isSubmitting ? <CircularProgress size={24}/> : 'Finish & Submit') : 'Next Section'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Paper>
                        );
                    })}
                </Box>
            </Paper>
        </Container>
    );
}

export default SelfPerceptionInventorySurvey;
